/**
 * Shapes.js
 * 
 * This module provides functions for creating, manipulating, and offsetting geometric shapes
 * in the Eagle Eye Designer application. It handles operations such as:
 * 
 * 1. Creating rectangular and circular geometries on 3D planes
 * 2. Finding centers and radii of shapes
 * 3. Adding offset boundaries to obstacles (safety margins)
 * 4. Converting 2D screen coordinates to 3D world coordinates
 * 
 * These functions support obstacle creation and manipulation on roof segments.
 */

import * as THREE from "three";
import Earcut from "earcut"; // Library for triangulating polygons
import { COLORS, LINES } from "../Global/materials";
import { createSegmentPlane } from "../Global/functions";
import { addExtrude } from "./Modifications";
import { getVertices } from "../Panels/Helpers";

/**
 * Creates a rectangular geometry on a 3D plane based on 2D screen coordinates
 * 
 * This function converts 2D screen coordinates (where the user has clicked and dragged)
 * into a 3D rectangle on the segment plane. It handles various cases of dragging direction.
 * 
 * @param {THREE.Vector2} startPoint - Start point in screen coordinates
 * @param {THREE.Vector2} endPoint - End point in screen coordinates
 * @param {THREE.Plane} facetPlane - The plane of the roof segment
 * @param {THREE.Raycaster} rayCaster - Raycaster for screen-to-world conversion
 * @param {THREE.Camera} camera - The camera for raycasting
 * @returns {Object} Object containing the points array and the geometry
 */
export function createRecGeo(startPoint, endPoint, facetPlane, rayCaster, camera) {
    // Avoid zero dimensions by adding a tiny value if needed
    if (startPoint.x === endPoint.x) {
        endPoint.x += Number.EPSILON;
    }

    if (startPoint.y === endPoint.y) {
        endPoint.y += Number.EPSILON;
    }

    // Calculate dimensions in screen space
    const left = Math.min(startPoint.x, endPoint.x);
    const right = Math.max(startPoint.x, endPoint.x);
    const top = Math.min(startPoint.y, endPoint.y);
    const bottom = Math.max(startPoint.y, endPoint.y);
    const width = right - left;
    const height = bottom - top;

    // Determine drag direction
    let dx = startPoint.x - endPoint.x < 0 ? -1 : 1;
    let dy = startPoint.y - endPoint.y < 0 ? -1 : 1;

    // Define the four corners of the rectangle in screen space
    // Default case: dragging from top-left to bottom-right or bottom-right to top-left
    let _vecTopLeft = new THREE.Vector3(startPoint.x, startPoint.y, 0);
    let _vecDownRight = new THREE.Vector3(endPoint.x, endPoint.y, 0);
    let _vecTopRight = new THREE.Vector3(
        startPoint.x - dx * width,
        startPoint.y,
        0
    );
    let _vecDownLeft = new THREE.Vector3(
        startPoint.x,
        startPoint.y - dy * height,
        0
    );

    // Handle alternate case: dragging from top-right to bottom-left or bottom-left to top-right
    if (dy === dx) {
        _vecTopLeft.set(startPoint.x, startPoint.y - dy * height, 0);
        _vecDownRight.set(startPoint.x - dx * width, startPoint.y, 0);
        _vecTopRight.set(endPoint.x, endPoint.y, 0);
        _vecDownLeft.set(startPoint.x, startPoint.y, 0);
    }

    // Vectors to store the 3D world coordinates of the rectangle corners
    let topleft = new THREE.Vector3();
    let topright = new THREE.Vector3();
    let bottomleft = new THREE.Vector3();
    let bottomright = new THREE.Vector3();

    // Convert screen coordinates to 3D points on the segment plane using raycasting
    rayCaster.setFromCamera(_vecTopLeft, camera);
    rayCaster.ray.intersectPlane(facetPlane, topleft);
    rayCaster.setFromCamera(_vecTopRight, camera);
    rayCaster.ray.intersectPlane(facetPlane, topright);
    rayCaster.setFromCamera(_vecDownLeft, camera);
    rayCaster.ray.intersectPlane(facetPlane, bottomleft);
    rayCaster.setFromCamera(_vecDownRight, camera);
    rayCaster.ray.intersectPlane(facetPlane, bottomright);
    
    // Create an array of points forming the rectangle (closing the loop)
    let points = [topleft, topright, bottomright, bottomleft, topleft];
    
    // Create a THREE.js buffer geometry from the points
    let g = new THREE.BufferGeometry().setFromPoints([
        topleft,
        topright,
        bottomright,
        bottomleft,
        topleft,
    ]);

    return { points, g };
}

/**
 * Creates a circular geometry on a 3D plane based on 2D screen coordinates
 * 
 * This function converts 2D screen coordinates (where the user has clicked and dragged)
 * into a 3D circle on the segment plane. The start point defines the center and the
 * distance to the end point defines the radius.
 * 
 * @param {THREE.Vector2} startPoint - Center point in screen coordinates
 * @param {THREE.Vector2} endPoint - Point on circumference in screen coordinates
 * @param {THREE.Plane} facetPlane - The plane of the roof segment
 * @param {THREE.Raycaster} rayCaster - Raycaster for screen-to-world conversion
 * @param {THREE.Camera} camera - The camera for raycasting
 * @returns {Object} Object containing the points array and the geometry
 */
export function createCirGeo(startPoint, endPoint, facetPlane, rayCaster, camera) {
    const points = [];
    
    // Center of the circle in screen space
    let centerX = startPoint.x;
    let centerY = startPoint.y;
    
    // Calculate the radius in screen space
    const radius = startPoint.distanceTo(endPoint);

    // Calculate delta from center to end point
    const deltaX = endPoint.x - centerX;
    const deltaY = endPoint.y - centerY;
    
    // Adjust the radius for better usability
    let dynamicRadius = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    dynamicRadius += radius;

    // Account for camera aspect ratio to ensure circles look circular
    const aspectRatio = camera.aspect;
    
    // Define number of segments for the circle (higher = smoother)
    let segments = 32;
    
    // Generate points around the circle
    for (let i = 0; i <= segments; i++) {
        const theta = (i / segments) * Math.PI * 2;
        
        // Adjust radius for aspect ratio
        const adjustedRadius = dynamicRadius / aspectRatio;
        
        // Calculate point in screen space
        const x = centerX + adjustedRadius * Math.cos(theta);
        const y = centerY + dynamicRadius * Math.sin(theta);

        // Create temporary vector for the point
        let temp = new THREE.Vector3(x, y, 0);

        // Convert screen coordinates to 3D point on the segment plane
        let newpoint = new THREE.Vector3();
        rayCaster.setFromCamera(temp, camera);
        rayCaster.ray.intersectPlane(facetPlane, newpoint);

        // Add the 3D point to the points array
        points.push(newpoint);
    }
    
    // Close the loop by adding the first point again
    points.push(points[0]);

    // Create a THREE.js buffer geometry from the points
    let geo = new THREE.BufferGeometry().setFromPoints(points);
    
    return { points, geo };
}

/**
 * Finds the center of a mesh using its bounding box
 * 
 * @param {THREE.Mesh} mesh - The mesh to find the center of
 * @returns {THREE.Vector3} The center point of the mesh
 */
export function findCenter(mesh) {
    // Create a bounding box from the mesh
    const bbox = new THREE.Box3().setFromObject(mesh);
    
    // Get the center of the bounding box
    const center = new THREE.Vector3();
    bbox.getCenter(center);

    return center;
}

/**
 * Calculates the distance (radius) from a center point to another point
 * 
 * @param {THREE.Vector3} center - The center point
 * @param {THREE.Vector3} point - The point to measure distance to
 * @returns {number} The distance between the points
 */
function getRadius(center, point) {
    const p = new THREE.Vector3(point.x, point.y, point.z);
    const radius = center.distanceTo(p);
    return radius;
}

/**
 * Adds an offset boundary to an obstacle mesh
 * 
 * This function creates a visual boundary around obstacles to represent
 * safety margins that panels should not intersect with. It handles both
 * circular and rectangular obstacles differently.
 * 
 * @param {THREE.Mesh} mesh - The obstacle mesh to add offset to
 * @param {Array<THREE.Vector3>} points - Array of points defining the obstacle shape
 * @param {string} type - Type of obstacle ("circle" or "rectangle")
 * @param {THREE.Shape} shape - The 2D shape of the obstacle
 * @param {number} offset - Offset distance in inches
 * @param {number} height - Height of the obstacle
 * @param {number} segmentIndex - Index of the segment containing the obstacle
 * @returns {Object} Object containing the updated mesh, shape, and points
 */
export function addOffset(mesh, points, type, shape, offset, height, segmentIndex) {
    // Get the segment plane
    const plane = createSegmentPlane(segmentIndex);
    
    // Initialize variables
    let offsetGeo = new THREE.BufferGeometry();
    let center = null;
    let tempExtrude = null;
    let radius = null;
    
    // Handle different obstacle types
    switch (type) {
        case "circle":
            // Create a temporary extruded mesh to work with
            tempExtrude = addExtrude(points, shape, 0, COLORS.maroon, mesh.parent);
            
            // Find the center of the circle
            center = findCenter(tempExtrude);
            
            // Calculate the original radius of the circle
            const obsRadius = getRadius(center, points[0]);
            radius = obsRadius;
            
            // Create a circular geometry with the offset added to the radius
            // Convert inches to meters with 0.0254 factor
            offsetGeo = new THREE.CircleGeometry(obsRadius + (offset * 0.0254));
            
            // Clean up the temporary mesh
            tempExtrude.removeFromParent();
            tempExtrude.geometry.dispose();
            tempExtrude.material.dispose();
            break;

        case "rectangle":
            // Create a temporary extruded mesh to work with
            tempExtrude = addExtrude(points, shape, 0, COLORS.maroon, mesh.parent);
            
            // Find the center of the rectangle
            center = findCenter(tempExtrude);

            // Get the corner points of the rectangle
            const p1 = new THREE.Vector3(points[0].x, points[0].y, points[0].z);
            const p2 = new THREE.Vector3(points[1].x, points[1].y, points[1].z);
            const p3 = new THREE.Vector3(points[3].x, points[3].y, points[3].z);
            
            // Calculate offset vectors along the x and y axes of the rectangle
            // Convert inches to meters with 0.0254 factor
            const x = new THREE.Vector3().subVectors(p2, p1).normalize().multiplyScalar(offset * 0.0254);
            const y = new THREE.Vector3().subVectors(p1, p3).normalize().multiplyScalar(offset * 0.0254);

            // Calculate direction vectors from center to each corner
            const directions = points.map((point) => {
                const corner = new THREE.Vector3(point.x, point.y, point.z);
                const direction = new THREE.Vector3().subVectors(corner, center);
                return direction;
            });

            // Define offset adjustments for each corner
            // The combination of negated and non-negated vectors ensures the rectangle
            // is expanded outward in all directions
            const addOffsets = [
                [x.clone().negate(), y],                // Top-left
                [x, y],                                 // Top-right
                [x, y.clone().negate()],                // Bottom-right
                [x.clone().negate(), y.clone().negate()], // Bottom-left
                [x.clone().negate(), y]                 // Close the loop (repeat first point)
            ];
            
            // Apply offsets to calculate new corner positions
            let corners = directions.map((direction, index) => {
                direction.add(addOffsets[index][0]);
                direction.add(addOffsets[index][1]);
                return tempExtrude.worldToLocal(direction);
            });
            corners.pop(); // Remove the duplicate closing point
            
            // Prepare data for triangulation using Earcut
            // Convert array of Vector3 to flat array of coordinates
            const verticesFlat = corners.reduce((acc, vertex) => acc.concat([vertex.x, vertex.y, vertex.z]), []);
            
            // Triangulate the polygon
            const earCutData = Earcut(verticesFlat, null, 3);
            const indices = new Uint32Array(earCutData);
            
            // Create a buffer geometry from the triangulated data
            offsetGeo.setIndex(new THREE.BufferAttribute(indices, 1));
            offsetGeo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(verticesFlat), 3));

            // Clean up the temporary mesh
            tempExtrude.removeFromParent();
            tempExtrude.geometry.dispose();
            tempExtrude.material.dispose();
            break;
    }

    // Create a wireframe outline for the offset boundary
    const wireframe = new THREE.EdgesGeometry(offsetGeo);
    const newOffset = new THREE.LineSegments(wireframe, LINES.orangeMaterial);
    newOffset.position.set(center.x, center.y, center.z);

    // Disable raycast interactions with the offset boundary
    newOffset.raycast = () => { };

    // Set visual properties for the offset boundary
    newOffset.material.transparent = true;
    newOffset.material.opacity = 1;
    newOffset.renderOrder = 1; // Higher order to render on top of other objects
    newOffset.material.depthTest = false; // Always visible, even behind other objects
    newOffset.name = "offset"; // Name for later reference

    // Add the offset boundary to the obstacle mesh
    mesh.add(newOffset);

    // Special handling for circular obstacles to create an extruded 3D shape
    if (radius != null) {
        // Rotate the offset boundary to match the segment plane
        const quaternion = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), plane.normal);
        newOffset.setRotationFromQuaternion(quaternion);

        // Create a temporary circle for getting the points in the correct orientation
        const geo = new THREE.CircleGeometry(radius);
        const wf = new THREE.EdgesGeometry(geo);
        const temp = new THREE.LineSegments(wf, LINES.orangeMaterial);
        temp.position.set(center.x, center.y, center.z);

        // Rotate the temporary circle to match the segment plane
        const quat = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), plane.normal);
        temp.setRotationFromQuaternion(quat);

        // Get the vertices of the temporary circle in world space
        const vecs = getVertices(temp);
        const updarr = [];
        for (let i = 0; i < vecs.length; i += 2) {
            const vec = newOffset.localToWorld(vecs[i]);
            updarr.push(vec);
        }
        updarr.push(updarr[0]); // Close the loop

        // Create a 2D shape from the 3D points (projecting onto XY plane)
        const shapePoints = updarr.map(point => new THREE.Vector2(point.x, point.y));
        const newShape = new THREE.Shape(shapePoints);
        
        // Create an extruded 3D mesh from the shape
        const extrudeMesh = addExtrude(updarr, newShape, height, COLORS.maroon, mesh.parent);

        // Clean up the temporary objects and original mesh
        newOffset.removeFromParent();
        temp.material.dispose();
        temp.geometry.dispose();
        mesh.removeFromParent();
        mesh.geometry.dispose();
        mesh.material.dispose();

        // Add the offset boundary to the new extruded mesh
        extrudeMesh.add(newOffset);

        return { mesh: extrudeMesh, shape: newShape, points: updarr };
    }

    // For rectangular obstacles, just return the original mesh with the added offset boundary
    return { mesh: mesh, shape: shape, points: points };
}